import * as React from 'react';
import { useSelector } from 'react-redux';
import { FavouriteDefinition, TextCollection } from 'app/types-shared';
import { STATE } from 'app/constants';
import { filtersTranslations } from './constants';
import { FiltersPanel } from 'app/components/filters/filters-panel/filters-panel';
import { mapToAdvancedSearchOptions } from './filters-mapping';
import { ButtonInline } from 'ui';
import { useTranslation } from 'app/infrastructure/translate/hooks';
import { 
  useActionCreators, 
  useFilterEventHandlers, 
  useLoadFiltersAndAdvancedSearchTypesEffect, 
  useUpdateConditionsAndConnectionsFromSimpleFormEffect, 
  useUpdateFiltersFromDataEffect, 
  useUserProfile,
  useInstanceSimpleForm
} from 'app/area/filters/hooks';
import { selectAdvancedSearchFields, selectAdvancedSearchTypes, selectCurrentList, selectFilterDomains } from 'app/area/filters/selectors';
import { useInstanceKey } from 'app/area/favourite/favourite-context';
import styles from './filters.scss';
import { ReactNode } from 'react';
import { reportTypes } from 'app/area/favourite/constants';

export interface FiltersProps {
  definition: FavouriteDefinition;
  updateDefinition: (transform?: (definition: FavouriteDefinition) => FavouriteDefinition) => void;
  children?: ReactNode;
}

export function FiltersContainer({
  definition,
  updateDefinition,
  children
}: FiltersProps) {

  const instanceKey = useInstanceKey();

  const translation = useTranslation(STATE.filters, filtersTranslations);
  const texts: TextCollection = useSelector(state => state[STATE.filters]?.texts);

  const {
    simpleForm,
    validate,
    validation,
    SimpleField
  } = useInstanceSimpleForm({ texts });

  const userProfile = useUserProfile();
  
  const currentList = useSelector(selectCurrentList(instanceKey));
  const advancedSearchFields = useSelector(selectAdvancedSearchFields(instanceKey));
  const advancedSearchTypes = useSelector(selectAdvancedSearchTypes(instanceKey));
  const filterDomains = useSelector(selectFilterDomains(instanceKey));

  const {
    loadAdvancedSearchFields,
    setFilterOption,
    setFilterOpenState
  } = useActionCreators();

  const {
    onFilterMenuOption,
    onAddFilters,
    onRemoveFilters,
    clear
  } = useFilterEventHandlers(simpleForm, currentList, advancedSearchTypes);

  const selectedReport = definition?.reports?.find(x=>x.report?.v === definition.selectedReport?.v);
  const selectedReportType = selectedReport?.report?.v;
  const selectedReportId =  selectedReportType === reportTypes.dashboard && selectedReport?.dashboard?.v;

  useLoadFiltersAndAdvancedSearchTypesEffect(selectedReportType, selectedReportId);
  useUpdateFiltersFromDataEffect(definition, filterDomains, advancedSearchTypes, translation);
  useUpdateConditionsAndConnectionsFromSimpleFormEffect(
    updateDefinition, currentList, simpleForm?.editing?.values
  );

  return currentList && currentList.length > 0 ?
    <>
      <div className={styles.informationPanel}>
        {children}
        <div>
          <ButtonInline onClick={clear}>{translation.clear_filters}</ButtonInline>
        </div>
      </div>
      <FiltersPanel
        favouriteDefinition={definition}
        translation={translation}
        userProfile={userProfile}
        options={mapToAdvancedSearchOptions(advancedSearchFields)}
        validate={validate}
        onRemove={onRemoveFilters}
        editingValues={simpleForm?.editing?.values}
        filtersDefinition={currentList}
        onAdvancedSearchFields={loadAdvancedSearchFields}
        onAddFilters={onAddFilters}
        onFilterMenuOption={onFilterMenuOption}
        onFilterOptionType={setFilterOption}
        validation={validation}
        setOpenState={setFilterOpenState}
        SimpleField={SimpleField}
      />
    </>
    : null;
}
